import { DetectorModes } from "./createDetector";
import { ImageQuery } from "./imageQueries";
import { NamedItem } from "./shared";

export interface DetectorSet extends NamedItem {}

// Detector Object used on DetectorManagement page

// DataSection - DetectorItem
export interface DetectorBase {
  id: string;
  status: "ON" | "OFF";
  name: string;
  mode: DetectorModes;
  label_prompt: string;
  created_at: string;
  created_by: string;
  confidence_threshold: number;
  last_image_query: ImageQuery | null;
  is_explore_detector: boolean;
  mode_configuration?: ModeConfiguration;
}

export interface ModeConfiguration {
  id: string;
  predictor: string;
  max_count: number | null;
  max_num_bboxes: number | null;
  class_names?: string[] | null;
  value_max_length?: number;
}

// ReviewPageUtils: valid_bbox_labels, labeling_task
// CreatePosicheck: id, label_prompt, confidence_threshold

export interface Detector extends DetectorBase {
  type: string;
  escalation_type?: "STANDARD" | "NO_HUMAN_LABELING";
  evaluation_results: EvaluationResults | null;
  recent_ground_truth_label_date: string | null;
  group_name: string;
  labeling_task: string;

  patience_time: number;
  set: NamedItem;
  valid_bbox_labels: string[] | null;
  owner: NamedItem;
  ground_truths: {
    yes: number;
    no: number;
  };
  total_iqs: number;
}

export interface BoundingBoxModeConfiguration {
  max_num_bboxes?: number;
  class_name?: string;
}

export interface CountModeConfiguration {
  max_count?: number;
  class_name?: string;
}
export interface MultiClassModeConfiguration {
  class_names?: string[];
}

export interface CreateDetectorInput {
  name?: string;
  label_prompt?: string;
  predictor_set_name?: string;
  predictor_set_id?: string;
  confidence_threshold?: number;
  pipeline_config?: string;
  is_explore?: boolean;
  mode?: DetectorModes;
  mode_configuration?: CountModeConfiguration | MultiClassModeConfiguration | BoundingBoxModeConfiguration;
  valid_bbox_labels?: string[];
  labeling_task?: string;
}

export interface CreatePredictorResponse extends NamedItem {
  label_prompt: string;
  confidence_threshold: number;
}

export interface PredictorRef extends NamedItem {
  prompt: string;
  confidenceThreshold: number;
}

export interface MLConfigChoice {
  id: string;
  internal_name: string;
  external_name: string;
  external_description: string;
}

export interface EvaluationResults {
  balanced_accuracy__mean: number /* deprecated, please use kfold_pooled_balanced_accuracy below */;
  eval_timestamp: string;
  total_ground_truth_examples: number;
  total_positive_ground_truth_examples: number;
  total_negative_ground_truth_examples: number;
  total_labeled_examples: number;
  total_positive_labeled_examples: number;
  total_negative_labeled_examples: number;
  kfold_pooled__balanced_accuracy: number;
  kfold_pooled__balanced_accuracy__lcb: number;
  kfold_pooled__balanced_accuracy__ucb: number;
  kfold_pooled__positive_accuracy: number;
  kfold_pooled__positive_accuracy__lcb: number;
  kfold_pooled__positive_accuracy__ucb: number;
  kfold_pooled__negative_accuracy: number;
  kfold_pooled__negative_accuracy__lcb: number;
  kfold_pooled__negative_accuracy__ucb: number;
  balanced_system_accuracies: {
    [key: string]: number;
  };
  positive_system_accuracies: {
    [key: string]: number;
  };
  negative_system_accuracies: {
    [key: string]: number;
  };
  ml_answer_proportions: {
    [key: string]: number;
  };
  total_per_class_ground_truth_examples: Record<`${number}`, number>;
  total_per_class_labeled_examples: Record<`${number}`, number>;
  class_accuracies: Record<`class_accuracies__${number}__mean` | `class_accuracies__${number}__stddev`, number> | null;
}

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.9;

export interface PipelineModelDetails {
  id: string;
  pipeline_config: string;
  is_active_pipeline: boolean;
  is_edge_pipeline: boolean;
  is_unclear_pipeline: boolean;
  is_oodd_pipeline: boolean;
  is_enabled: boolean;
  created_at: string;
  trained_at: string | null;
  type: "active" | "shadow" | "unclear" | "oodd";
  friendly_name: string | null;
  model_binary_id: string | null;
  training_in_progress: {
    started_at: string;
    k8_pod: string;
  } | null;
  metrics: {
    revision_number: number | null;
    label_cnt: number | null;
    ground_truth_cnt: number | null;
    training_time_s: number | null;
    balanced_accuracy: number | null;
    positive_accuracy: number | null;
    negative_accuracy: number | null;
    num_classes_measured: number | null;
    worst_class_accuracy: number | null;
    mean_avg_precision: number | null;
    precision: number | null;
    recall: number | null;
  };
  eval_mlbinary_key: string | null;
  eval_mlbinary_revision_number: number | null;
  eval_mlbinary_friendly_name: string | null;
}

export interface PaginatedPipelineResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PipelineModelDetails[];
}

export interface DetectorPipelineDetails {
  pipelines: PipelineModelDetails[];
}
